<script setup lang="ts">
import IntroMessage from '@jaaluu/components/IntroMessage.vue';
import TextAndAudio from '@jaaluu/components/TextAndAudio.vue';
import TooltipChat from '@jaaluu/components/TooltipChat.vue';
import WordLinks from '@jaaluu/components/WordLinks.vue';
import { GAnalyticsEvent } from '@jaaluu/interfaces/GAnalyticEvent';
import stores from '@jaaluu/stores';
import { IntroMessageType } from '@jaaluu/stores/intro-message';
import { Tooltip } from '@jaaluu/stores/tooltip';
import FavGroupSelect from '@jaaluu/views/Favorites/FavGroupSelect.vue';
import Wordmap from '@jaaluu/views/Wordmap/Wordmap.vue';
import { t } from '@shared/i18n';
import { CourseService, Language, WordInfoService } from '@shared/openapi/jaaluu-api';
import sharedStores from '@shared/shared-stores';
import { PropType, Ref, computed, onMounted, ref, watch } from 'vue';
import AudioPlayer from './AudioPlayer.vue';
import TextWithTooltips from './TextWithTooltips.vue';

const props = defineProps({
  tooltip: {
    required: true,
    type: Object as PropType<Tooltip>
  }
});
const { langNative, langForeign } = stores.language.state;
const { favWords, activeGroupKey } = stores.favorites.state;
const { toggleFavoriteWord } = stores.favorites.funcs;
const { removeTooltip } = stores.tooltip.funcs;
const { handleDefaultError } = stores.error.funcs;
const { getImageFileUrl } = stores.files.funcs;
const { animate, animateAnime } = sharedStores.animate.funcs;
const { setIntroMsgSeen } = stores.introMessage.funcs;
const { sendEvent } = stores.gAnalytics.funcs;

const wordInfo = ref({
  translation: null,
  example: null,
  synonyms: null,
  extensions: [],
  definition: {
    native: null,
    foreign: null
  },
  baseWord: {
    native: null,
    foreign: null
  },
  antonyms: null,
  wordType: '',
  article: null,
  conjugations: null,
  grammer: null,
  grammerWord: null,
  etymology: null,
  image: {
    sentence: null,
    url: null
  },
  wordmap: false
});
const imageUrls: Ref<any> = ref([null, null, null]);
const wordImageFilename: Ref<string | null> = ref(null);
const examples: Ref<any> = ref([]);
const emoExamples: Ref<any> = ref(null);
const allMeanings: Ref<any> = ref(null);
const expansionPanelKeys: Ref<number | null> = ref(null);
const showContext = ref(false);
const expansionPanelKeysSentence: Ref<number | null> = ref(null);
const tooltipContentEng = ref('');
const maxHeights: Ref<any> = ref({});
const hideShowMoreButtons: Ref<string[]> = ref([]);
const hasMoreContent = ref(false);
const loadExplanation = ref(false);
const cleanContent = computed(() => {
  return props.tooltip.content.replace(/^[.,!?;:…\u2026]+|[.,!?;:…\u2026]+$/g, '').trim();
});

const isFromVocalList = computed(() => {
  if (!props.tooltip.event) return true;
  return false;
});

const resteValus = () => {
  console.log('resetting values');
  loadExplanation.value = false;
  expansionPanelKeys.value = null;
  imageUrls.value = [null, null, null];
  wordImageFilename.value = null;
  maxHeights.value = {};
  hideShowMoreButtons.value = [];
  examples.value = [];
  emoExamples.value = null;
  allMeanings.value = null;
  wordInfo.value = {
    translation: null,
    example: null,
    synonyms: null,
    definition: {
      native: null,
      foreign: null
    },
    baseWord: {
      native: null,
      foreign: null
    },
    grammer: null,
    grammerWord: null,
    etymology: null,
    antonyms: null,
    extensions: [],
    wordType: '',
    article: null,
    conjugations: null,
    image: {
      sentence: null,
      url: null
    },
    wordmap: false
  };
  tooltipContentEng.value = '';
  hasMoreContent.value = false;
};

// @Watch("$store.state.tooltip.open")
const getWordInfo = () => {
  resteValus();
  if (props.tooltip.open && langForeign.value && langNative.value) {
    const query: any = {
      q: cleanContent.value,
      context_sentence: props.tooltip.sentence,
      foreign: props.tooltip.isReverseTranslation ? langNative.value : langForeign.value,
      native: props.tooltip.isReverseTranslation ? langForeign.value : langNative.value
    };
    WordInfoService.getTranslationV1WordInfoTranslationGet({ query })
      .then((res: any) => {
        wordInfo.value.translation = res.data.translation;
        wordInfo.value.wordType = res.data.word_type.toLowerCase();
        if (props.tooltip.openedCount === 1) {
          localStorage.setItem('tooltipOpenedCount', props.tooltip.openedCount.toString());
          setTimeout(() => {
            animateAnime('#tooltip-word', { opacity: [0.8, 1], translateX: [50, 0] }, 1500);
            animate('#tooltip-word', 'color', 1500);
          }, 500);
          setTimeout(() => {
            animateAnime('#tooltip-translation', { opacity: [0.8, 1], translateX: [50, 0] }, 1500);
            animate('#tooltip-translation', 'color', 1500);
          }, 2000);
        }
      })
      .catch(handleDefaultError);
    WordInfoService.getImagesUnsplashV1WordInfoImagesGet({ query })
      .then((res: any) => {
        imageUrls.value = res.data.image_urls;
      })
      .catch(handleDefaultError);

    CourseService.getWordImageV1CourseWordImagePost({
      body: {
        word: cleanContent.value,
        context_sentence: props.tooltip.sentence ?? '',
        language: props.tooltip.isReverseTranslation ? langNative.value! : langForeign.value!
      }
    })
      .then((res: any) => {
        wordImageFilename.value = res.data?.image ?? null;
      })
      .catch(() => {
        wordImageFilename.value = null;
      });

    setArticle();
  } else {
    resteValus();
  }
};

const openSynonyms = () => {
  if (wordInfo.value.synonyms) return;
  if (!langForeign.value) return;
  const query = {
    q: cleanContent.value,
    foreign: langForeign.value
  };
  WordInfoService.getSynonymsV1WordInfoSynonymsGet({ query })
    .then((res: any) => {
      wordInfo.value.synonyms = res.data.synonyms;
    })
    .catch(handleDefaultError);
};

const openAntonyms = () => {
  if (wordInfo.value.antonyms) return;
  if (!langForeign.value) return;
  const query = {
    q: cleanContent.value,
    foreign: langForeign.value
  };
  WordInfoService.getAntonymsV1WordInfoAntonymsGet({ query })
    .then((res: any) => {
      wordInfo.value.antonyms = res.data.antonyms;
    })
    .catch(handleDefaultError);
};
const openDefinition = () => {
  if (wordInfo.value.definition.native) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    context_sentence: props.tooltip.sentence,
    foreign: langForeign.value,
    native: langNative.value
  };
  WordInfoService.getDefinitionV1WordInfoDefinitionGet({ query })
    .then((res: any) => {
      wordInfo.value.definition = res.data.definition;
    })
    .catch(handleDefaultError);
};

const openBaseWord = () => {
  if (wordInfo.value.baseWord.native) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    context_sentence: props.tooltip.sentence,
    foreign: langForeign.value,
    native: langNative.value
  };
  WordInfoService.getBaseWordV1WordInfoBaseWordGet({ query })
    .then((res: any) => {
      wordInfo.value.baseWord = res.data.base_word;
    })
    .catch(handleDefaultError);
};

const setArticle = () => {
  if (wordInfo.value.article) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    context_sentence: props.tooltip.sentence,
    foreign: langForeign.value,
    native: langNative.value
  };
  WordInfoService.getArticleV1WordInfoArticleGet({ query })
    .then((res: any) => {
      wordInfo.value.article = res.data.article.toLowerCase();
    })
    .catch(handleDefaultError);
};

const openConjunctions = () => {
  if (wordInfo.value.conjugations) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    context_sentence: props.tooltip.sentence,
    foreign: langForeign.value,
    native: langNative.value
  };
  WordInfoService.getConjugationsV1WordInfoConjugationsGet({ query })
    .then((res: any) => {
      wordInfo.value.conjugations = res.data.conjugations;
    })
    .catch(handleDefaultError);
};

const openGrammer = () => {
  if (wordInfo.value.extensions.length > 0) return;
  if (!langForeign.value || !langNative.value || !props.tooltip.sentence) return;
  const query = {
    q: props.tooltip.sentence,
    foreign: langForeign.value,
    native: langNative.value
  };
  WordInfoService.getGrammerV1WordInfoGrammerGet({ query })
    .then((res: any) => {
      wordInfo.value.grammer = res.data.grammer;
    })
    .catch(handleDefaultError);
};

const loadGrammerWord = () => {
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    foreign: langForeign.value,
    native: langNative.value,
    context_sentence: props.tooltip.sentence
  };
  WordInfoService.getGrammerWordV1WordInfoGrammerWordGet({ query })
    .then((res: any) => {
      wordInfo.value.grammerWord = res.data.grammer;
    })
    .catch(handleDefaultError);
};

const openEtymology = () => {
  if (wordInfo.value.etymology) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    foreign: langForeign.value,
    native: langNative.value,
    context_sentence: props.tooltip.sentence
  };
  WordInfoService.getEtymologyV1WordInfoEtymologyGet({ query })
    .then((res: any) => {
      wordInfo.value.etymology = res.data.etymology;
    })
    .catch(handleDefaultError);
};

const openAllMeanings = () => {
  if (allMeanings.value) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    foreign: langForeign.value,
    native: langNative.value,
    context_sentence: props.tooltip.sentence
  };
  WordInfoService.getAllMeaningsV1WordInfoAllMeaningsGet({ query })
    .then((res: any) => {
      allMeanings.value = res.data.meanings;
    })
    .catch(handleDefaultError);
};

/* const openImage = () => {
  if (wordInfo.value.image.sentence) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    foreign: langForeign.value
  };
  WordInfoService.getImageSentenceV1WordInfoImageSentenceGet({ query })
    .then((res: any) => {
      wordInfo.value.image.sentence = res.data.sentence;
      wordInfo.value.image.url = res.data.url;
    })
    .catch(handleDefaultError);
}; */

const openWordmap = () => {
  wordInfo.value.wordmap = true;
};

const atUpdateExpansionPanel = () => {
  if (expansionPanelKeys.value === undefined || expansionPanelKeys.value === null) return;
  const panels = [
    openExamples, // 0 examples
    openDefinition, // 1 definition
    openBaseWord, // 2 base word
    openAllMeanings, // 3 all meanings
    loadGrammerWord, // 4 grammar
    openEtymology, // 5 etymology
    () => sendEvent(GAnalyticsEvent.OPEN_LANGUAGE_TEACHER, { source: 'tooltip' }), // 6 language teacher
    openEmotionalExamples, // 7
    openSynonyms, // 8
    openAntonyms, // 9
    openConjunctions, // 10
    openWordmap // 11
  ];
  expansionPanelKeys.value.forEach((key: number) => {
    panels[key]();
  });
};
const atUpdateExpansionPanelSentence = () => {
  if (expansionPanelKeysSentence.value === undefined || expansionPanelKeysSentence.value === null)
    return;
  console.log('expansionPanelKeysSentence', expansionPanelKeysSentence.value);
  const panels = [openGrammer];
  panels[expansionPanelKeysSentence.value]();
};

const openExamples = () => {
  if (examples.value.length > 0) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    context_sentence: props.tooltip.isReverseTranslation ? null : props.tooltip.sentence,
    foreign: props.tooltip.isReverseTranslation ? langNative.value : langForeign.value,
    native: props.tooltip.isReverseTranslation ? langForeign.value : langNative.value,
    count: 3
  };
  WordInfoService.getExamplesV1WordInfoExamplesGet({ query })
    .then((res: any) => {
      examples.value = res.data.examples;
    })
    .catch(handleDefaultError);
};

const openEmotionalExamples = () => {
  if (emoExamples.value !== null) return;
  if (!langForeign.value || !langNative.value) return;
  const query = {
    q: cleanContent.value,
    context_sentence: props.tooltip.isReverseTranslation ? null : props.tooltip.sentence,
    foreign: props.tooltip.isReverseTranslation ? langNative.value : langForeign.value,
    native: props.tooltip.isReverseTranslation ? langForeign.value : langNative.value
  };
  WordInfoService.getEmotionalExamplesV1WordInfoEmotionalExamplesGet({ query })
    .then((res: any) => {
      emoExamples.value = res.data.examples;
    })
    .catch(handleDefaultError);
};

const getHtml = (text: string) => {
  // Regular expression to extract content regardless of the format string
  const pattern = /```(.*?)\n([\s\S]*?)\n```/;

  // Match the pattern
  const match = text.match(pattern);

  if (match) {
    return match[2]; // The second group is the content
  }
};

const wordTypeIsNoun = computed(() => {
  if (!wordInfo.value.wordType) return false;
  return wordInfo.value.wordType?.toLowerCase() === 'noun';
});

const wordTypeIsVerb = computed(() => {
  if (!wordInfo.value.wordType) return false;
  return wordInfo.value.wordType?.toLowerCase() === 'verb';
});

const isFavorite = computed(() => {
  return (
    favWords.value.filter(
      (fw) =>
        fw.word.toLowerCase() === cleanContent.value.toLowerCase() &&
        wordInfo.value.translation &&
        fw.translations
          .map((t) => t.toLowerCase())
          .includes(wordInfo.value.translation.toLowerCase()) &&
        fw.wordType === (props.tooltip.isCompleteSentence ? 'sentence' : wordInfo.value.wordType)
    ).length > 0
  );
});

const isMeaningFavorite = (meaning: any) => {
  return (
    favWords.value.filter(
      (fw) =>
        fw.word.toLowerCase() === meaning.word.toLowerCase() &&
        fw.translations.map((t) => t.toLowerCase()).includes(meaning.translation.toLowerCase()) &&
        fw.wordType === meaning.word_type.toLowerCase()
    ).length > 0
  );
};

const toggleMeaningFavorite = (meaning: any) => {
  if (!langForeign.value || !langNative.value) return;
  setIntroMsgSeen(IntroMessageType.SAVE_WORD_AS_FAV);
  toggleFavoriteWord(
    meaning.word,
    langForeign.value,
    langNative.value,
    meaning.word_type.toLowerCase(),
    meaning.translation,
    -1,
    true,
    activeGroupKey.value,
    props.tooltip.sentence
  );
};

onMounted(() => {
  sendEvent(GAnalyticsEvent.OPEN_TOOLTIP, { word: cleanContent.value });
  getWordInfo();
});

watch(
  () => props.tooltip.content,
  () => getWordInfo()
);
</script>

<template>
  <v-card class="relative tooltip-card" elevation="0" v-if="langForeign && langNative">
    <div v-if="!isFromVocalList">
      <div class="d-flex overflow-x-hidden">
        <div v-for="(imageUrl, index) in imageUrls.slice(0, 3)" :key="index" class="image_33">
          <v-img height="100" :src="imageUrl" v-if="imageUrl" cover> </v-img>
          <v-skeleton-loader
            v-else
            height="100"
            tile
            class="skeleton-image"
            type="image"
          ></v-skeleton-loader>
        </div>
      </div>
      <v-btn
        class="close-icon"
        size="x-small"
        icon
        @click="() => removeTooltip(tooltip.idx)"
        color="secondary"
      >
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </div>

    <v-card-text class="overflow-auto pa-0">
      <div class="d-flex align-baseline justify-space-between mt-4">
        <div
          class="w-100"
          :class="{
            'd-flex align-center': !props.tooltip.isCompleteSentence,
            'mt-4': imageUrls.length === 0
          }"
        >
          <div class="d-flex justify-space-between align-end">
            <div class="d-flex align-center ml-2" :class="{ 'justify-center': isFromVocalList }">
              <div class="title-16 bold d-flex align-center" id="tooltip-word">
                <AudioPlayer
                  :sentences-list="[cleanContent]"
                  :show-listen-text="false"
                  @audio-started="() => setIntroMsgSeen(IntroMessageType.PLAY_WORD)"
                />
                <div class="d-flex ws-nowrap w-100">
                  <TextWithTooltips
                    :text="cleanContent"
                    :is-complete-sentence="props.tooltip.isCompleteSentence"
                    :context-sentence="props.tooltip.sentence"
                    :no-break="!props.tooltip.isCompleteSentence"
                  />
                </div>
              </div>
            </div>
          </div>
          <!-- Content -->
          <div
            v-if="wordInfo.translation && langForeign"
            class="pl-2"
            :class="{ 'mt-4 ml-4': props.tooltip.isCompleteSentence }"
          >
            <div
              class="subtitle-2 native-text align-baseline d-flex align-center"
              :id="isFromVocalList ? 'vocab-word' : 'tooltip-translation'"
              :class="{ 'justify-center': isFromVocalList }"
            >
              <div class="bold">{{ wordInfo.translation }}</div>
            </div>
          </div>

          <v-skeleton-loader
            v-else
            height="20"
            width="100"
            class="skeleton-no-margin ml-2"
            type="text"
          ></v-skeleton-loader>
        </div>

        <div class="d-flex align-center">
          <v-btn
            variant="text"
            size="small"
            v-if="langForeign && wordInfo.translation"
            @click="
              () => {
                setIntroMsgSeen(IntroMessageType.SAVE_WORD_AS_FAV);
                if (wordInfo.translation) {
                  toggleFavoriteWord(
                    cleanContent,
                    langForeign!,
                    langNative!,
                    tooltip.isCompleteSentence ? 'sentence' : wordInfo.wordType,
                    wordInfo.translation,
                    -1,
                    true,
                    activeGroupKey,
                    props.tooltip.sentence
                  );
                }
              }
            "
            color="green"
            :icon="isFavorite ? 'mdi-star' : 'mdi-star-outline'"
          />
          <WordLinks class="ml-4" :word="cleanContent" :tooltip-idx="tooltip.idx" />
          <FavGroupSelect :z-overlay="true" class="mr-2 mb-2 ml-2" />
        </div>
      </div>

      <div class="d-flex align-center justify-space-between">
        <div class="mb-4 ml-4 d-flex align-center">
          <div v-if="!tooltip.isCompleteSentence" class="word-type subtitle">
            {{ wordInfo.wordType }}
          </div>
          <div
            class="subtitle ws-nowrap"
            v-if="
              !tooltip.isCompleteSentence && wordTypeIsNoun && ![Language.ZH].includes(langForeign)
            "
          >
            , {{ wordInfo.article }}
          </div>
        </div>

        <v-btn
          v-if="props.tooltip.sentence && !tooltip.isCompleteSentence"
          variant="text"
          size="x-small"
          class="ml-2 float-right"
          @click="showContext = !showContext"
        >
          {{ t('tooltip.context') }}
        </v-btn>
      </div>

      <div v-if="props.tooltip.sentence && showContext" class="mb-4 ml-4 subtitle">
        {{ props.tooltip.sentence }}
      </div>

      <IntroMessage :type="IntroMessageType.SAVE_WORD_AS_FAV" icon="mdi-star" class="mt-2" />
      <IntroMessage :type="IntroMessageType.PLAY_WORD" icon="mdi-play" class="mt-2 mb-2" />
      <div v-if="wordImageFilename" class="d-flex justify-center mt-2 mb-1">
        <v-img
          :src="getImageFileUrl(wordImageFilename, 'course')"
          :max-height="160"
          :max-width="220"
          contain
        />
      </div>

      <div v-if="!isFromVocalList">
        <v-expansion-panels
          multiple
          variant="accordion"
          v-model="expansionPanelKeys"
          v-if="!tooltip.isCompleteSentence && !tooltip.isReverseTranslation"
          @update:modelValue="atUpdateExpansionPanel"
        >
          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-text-short" size="14" class="mr-2" />{{
                t('tooltip.examples')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div v-for="example in examples" :key="example.native" class="mb-3">
                <div><TextAndAudio :text="example.foreign" /></div>
                <div class="native-text mt-0">{{ example.native }}</div>
                <v-divider class="my-3" v-if="example !== examples[examples.length - 1]" />
              </div>
              <v-skeleton-loader
                v-if="examples.length < 3"
                v-for="_ in Array.from({ length: 3 }, (_, i) => i + 1)"
                height="50"
                class=""
                type="sentences"
              ></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-exclamation" size="14" class="mr-2" />{{
                t('tooltip.definition')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div class="" v-if="wordInfo.definition.foreign">
                <div><TextAndAudio :text="wordInfo.definition.foreign" /></div>
                <div class="native-text mt-1">{{ wordInfo.definition.native }}</div>
              </div>
              <v-skeleton-loader v-else height="50" class="" type="sentences"></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-book-alphabet" size="14" class="mr-2" />{{
                t('tooltip.base_word')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div class="" v-if="wordInfo.baseWord.foreign">
                <div><TextAndAudio :text="wordInfo.baseWord.foreign" /></div>
                <div class="native-text mt-1">{{ wordInfo.baseWord.native }}</div>
              </div>
              <v-skeleton-loader v-else height="50" class="" type="sentences"></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-format-list-bulleted" size="14" class="mr-2" />{{
                t('tooltip.all_meanings')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div v-if="allMeanings">
                <div v-for="(meaning, idx) in allMeanings" :key="idx" class="mb-3">
                  <div class="d-flex align-baseline justify-space-between">
                    <div class="d-flex align-baseline">
                      <div class="bold"><TextAndAudio :text="meaning.word" /></div>
                      <div class="native-text ml-1">- {{ meaning.word_type }}</div>
                    </div>
                    <v-btn
                      variant="text"
                      size="small"
                      density="compact"
                      @click="() => toggleMeaningFavorite(meaning)"
                      color="green"
                      :icon="isMeaningFavorite(meaning) ? 'mdi-star' : 'mdi-star-outline'"
                    />
                  </div>
                  <div class="native-text mt-0">{{ meaning.translation }}</div>
                  <div class="mt-1">{{ meaning.definition }}</div>
                  <div class="native-text mt-1 font-italic">{{ meaning.example }}</div>
                  <v-divider class="my-3" v-if="idx !== allMeanings.length - 1" />
                </div>
              </div>
              <v-skeleton-loader
                v-else
                height="200"
                class=""
                type="paragraph,paragraph"
              ></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-book-open" size="14" class="mr-2" />{{
                t('tooltip.grammer')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div v-if="wordInfo.grammerWord">
                <div v-html="getHtml(wordInfo.grammerWord)"></div>
                <TextAndAudio :text="wordInfo.grammerWord" :play-icon-on-top="true" />
              </div>
              <v-skeleton-loader
                v-else
                height="200"
                class=""
                type="paragraph,paragraph"
              ></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-history" size="14" class="mr-2" />{{
                t('tooltip.etymology')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div v-if="wordInfo.etymology">
                <div v-html="getHtml(wordInfo.etymology)"></div>
                <TextAndAudio :text="wordInfo.etymology" :play-icon-on-top="true" />
              </div>
              <v-skeleton-loader
                v-else
                height="200"
                class=""
                type="paragraph,paragraph"
              ></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-robot-outline" size="14" class="mr-2" />{{
                t('chat.language_teacher')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <TooltipChat :word="cleanContent" :context-sentence="tooltip.sentence ?? undefined" />
            </v-expansion-panel-text>
          </v-expansion-panel>
          <!-- <v-expansion-panel :class="{ 'no-height': !wordTypeIsNoun }">
            <v-expansion-panel-title
              ><v-icon icon="mdi-image" size="14" class="mr-2" />{{
                t('general.image')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div v-if="wordInfo.image.sentence">
                <div class="d-flex justify-center mb-3">
                  <TextAndAudio :text="wordInfo.image.sentence" />
                </div>
                <v-img
                  class="mt-2 mx-auto"
                  :max-width="300"
                  :src="getImageFileUrl(wordInfo.image.url)"
                  cover
                ></v-img>
              </div>
              <div class="skeleton-no-margin" v-else>
                <v-skeleton-loader height="20" type="text"></v-skeleton-loader>
                <v-skeleton-loader height="200" type="image"></v-skeleton-loader>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel> -->

          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-heart" size="14" class="mr-2" />{{
                t('tooltip.emotional_examples')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div v-if="emoExamples" class="py-2">
                <div
                  :key="def.type"
                  v-for="(def, defIdx) in [
                    { type: 'positive', icon: 'mdi-emoticon-happy-outline', color: 'green' },
                    { type: 'negative', icon: 'mdi-emoticon-sad-outline', color: 'red' },
                    { type: 'joke', icon: 'mdi-emoticon-lol-outline', color: 'blue' }
                  ]"
                >
                  <div class="d-flex align-center ml-n3">
                    <v-icon :icon="def.icon" :color="def.color" size="14" class="mr-3" />
                    <div>
                      <div>
                        <TextAndAudio :text="emoExamples[def.type].foreign.replaceAll('\n', '')" />
                      </div>
                      <div class="native-text">
                        {{ emoExamples[def.type].native.replaceAll('\n', '') }}
                      </div>
                    </div>
                  </div>
                  <v-divider v-if="defIdx < 2" class="my-4" />
                </div>
              </div>
              <v-skeleton-loader
                v-else
                v-for="_ in Array.from({ length: 3 }, (_, i) => i + 1)"
                height="50"
                class=""
                type="sentences"
              ></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-equal" size="14" class="mr-2" />{{
                t('tooltip.synonyms')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div class="d-flex align-center flex-wrap" v-if="wordInfo.synonyms">
                <div v-for="s in wordInfo.synonyms" :key="s" class="mr-4">
                  <TextAndAudio :text="s" class="mr-4" />
                </div>
              </div>
              <v-skeleton-loader v-else height="50" class="" type="sentences"></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-not-equal-variant" size="14" class="mr-2" />{{
                t('tooltip.antonyms')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div class="d-flex align-center flex-wrap" v-if="wordInfo.antonyms">
                <div v-for="a in wordInfo.antonyms" :key="a" class="mr-4">
                  <TextAndAudio :text="a" class="mr-4" />
                </div>
              </div>
              <v-skeleton-loader v-else height="50" class="" type="sentences"></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel v-if="wordTypeIsVerb">
            <v-expansion-panel-title
              ><v-icon icon="mdi-table" size="14" class="mr-2" />{{
                t('tooltip.conjugations')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text
              :class="{ 'expansion-text-no-padding': wordTypeIsVerb, 'no-height': wordTypeIsNoun }"
            >
              <div v-if="wordInfo.conjugations" class="pb-12 unformatted-content table-strong">
                <div v-html="getHtml(wordInfo.conjugations)"></div>
              </div>
              <v-skeleton-loader
                v-else
                height="100"
                class=""
                type="paragraph,paragraph"
              ></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-transit-connection-horizontal" size="14" class="mr-2" />{{
                t('general.wordmap')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <Wordmap :word="cleanContent" />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-expansion-panels
          v-model="expansionPanelKeysSentence"
          v-else-if="!tooltip.isReverseTranslation"
          @update:modelValue="atUpdateExpansionPanelSentence"
        >
          <v-expansion-panel>
            <v-expansion-panel-title
              ><v-icon icon="mdi-information-variant" size="14" class="mr-2" />{{
                t('tooltip.grammer')
              }}</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <div v-if="wordInfo.grammer">
                <div v-html="getHtml(wordInfo.grammer)"></div>
                <TextAndAudio :text="wordInfo.grammer" :play-icon-on-top="true" />
              </div>
              <v-skeleton-loader
                v-else
                height="200"
                class=""
                type="paragraph,paragraph"
              ></v-skeleton-loader>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>
.tooltip-card {
  z-index: $z-level-10;
  min-width: 300px;
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
}
.image_33 {
  width: 34% !important;
}
.image_50 {
  width: 50% !important;
}
.overflow-x-hidden {
  overflow-x: hidden;
}
audio {
  width: 111px;
  overflow: hidden;
  scale: 0.8;
}
h1 {
  margin-top: 30px;
}

h2 {
  margin-top: 20px;
}

h3 {
  margin-top: 10px;
  font-weight: 300;
}

h4 {
  margin-top: 10px;
}
.thumb {
  float: right;
  margin: 10px;
}
.close-icon {
  position: absolute !important;
  top: 10px;
  right: 10px;
  z-index: 1000;
}
dd {
  margin-left: 20px;
}
.wiktionary-html {
  overflow-y: hidden;
}
.v-expansion-panel-title {
  background-color: rgb(var(--v-theme-secondary));
  padding: 10px !important;
  min-height: 36px !important;
  border-radius: 0px !important;
}
.i-frame-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.i-frame {
  width: 100%;
  height: 70vh;
  flex-grow: 1;
  border: none;
  margin: 0;
  padding: 0;
}
.skeleton-image {
  border-right: 1px solid darkgray;
}
.native-text {
  color: darkgray;
}
.unformatted-content {
  overflow-x: auto;
}
.max-width-small {
  max-width: 550px;
}
.no-height {
  height: 0px !important;
  pointer-events: none;
  opacity: 0;
}
.inline {
  display: inline;
}
</style>
